type FbqFunction = {
  (...args: any[]): void
  callMethod?: (...args: any[]) => void
  queue?: any[]
  push?: (args: any[]) => void
  loaded?: boolean
  version?: string
}

type LeadTrackingOptions = {
  contentName?: string
  contentCategory?: string
  eventSourceUrl?: string
}

declare global {
  interface Window {
    fbq?: FbqFunction
    _fbq?: FbqFunction
  }
}

const META_PIXEL_SCRIPT_ID = 'meta-pixel-script'
const META_PIXEL_ID = '970579679073888'

let metaPixelInitialized = false

const hasMetaPixelId = () => Boolean(META_PIXEL_ID)

const ensureFbq = () => {
  if (window.fbq) {
    return window.fbq
  }

  const fbq: FbqFunction = function(...args: any[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args)
      return
    }

    if (!fbq.queue) {
      fbq.queue = []
    }

    fbq.queue.push(args)
  }

  if (!window._fbq) {
    window._fbq = fbq
  }

  fbq.push = (args: any[]) => {
    if (!fbq.queue) {
      fbq.queue = []
    }

    fbq.queue.push(args)
  }
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []
  window.fbq = fbq

  return fbq
}

export const initializeMetaPixel = () => {
  if (
    typeof window === 'undefined' ||
    !hasMetaPixelId() ||
    metaPixelInitialized
  ) {
    return
  }

  const fbq = ensureFbq()

  if (!document.getElementById(META_PIXEL_SCRIPT_ID)) {
    const script = document.createElement('script')
    script.id = META_PIXEL_SCRIPT_ID
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    document.head.appendChild(script)
  }

  fbq('init', META_PIXEL_ID)
  metaPixelInitialized = true
}

export const trackPageView = () => {
  if (!hasMetaPixelId() || typeof window === 'undefined' || !window.fbq) {
    return
  }

  window.fbq('track', 'PageView')
}

export const trackLead = (options: LeadTrackingOptions = {}) => {
  if (!hasMetaPixelId() || typeof window === 'undefined' || !window.fbq) {
    return null
  }

  const eventId = `lead_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`

  window.fbq(
    'track',
    'Lead',
    {
      content_name: options.contentName || 'button_click',
      content_category: options.contentCategory || 'lead_button',
      event_source_url: options.eventSourceUrl || window.location.href,
    },
    {
      eventID: eventId,
    },
  )

  return eventId
}

export const trackLeadLink = (
  event: MouseEvent,
  url: string,
  options: LeadTrackingOptions = {},
  delay = 300,
) => {
  if (
    !hasMetaPixelId() ||
    typeof window === 'undefined' ||
    !window.fbq ||
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return
  }

  event.preventDefault()

  const anchor =
    event.currentTarget instanceof HTMLAnchorElement
      ? event.currentTarget
      : null
  const target = anchor?.target || ''
  const shouldOpenInNewWindow = target === '_blank'
  let nextWindow: Window | null = null

  if (shouldOpenInNewWindow) {
    nextWindow = window.open('', '_blank', 'noopener,noreferrer')
    if (nextWindow) {
      nextWindow.opener = null
    }
  }

  trackLead(options)

  window.setTimeout(() => {
    if (shouldOpenInNewWindow) {
      if (nextWindow) {
        nextWindow.location.href = url
      } else {
        window.open(url, '_blank', 'noopener,noreferrer')
      }
      return
    }

    window.location.href = url
  }, delay)
}
